import { z } from 'zod';
import { FieldPerfRangeSchema, type FieldPerfRange } from './fieldPerf.schema.js';

// ============================================================
// FIELD-TABLET SCHEDULED GPS READINGS — the device contract (owner, 2026-10-29).
//
// NOT PUNCHES. A tablet takes a GPS reading every `gpsSampleEveryMin` minutes
// inside each of `gpsSampleWindows` (company wall-clock time), plus ONE
// reading `gpsSampleEveryMin` before each window opens, and uploads them so
// the console can show each tablet's GPS pattern (Devices → Performance, the GPS column).
//
// ── What the device is told — on every GET /api/field/sync page ─────────────
//
//   gpsSampleWindows:  [{ start: '05:30', end: '07:00' }, { start: '16:00', end: '20:00' }]
//   gpsSampleEveryMin: 5
//
// Company time (`companyTimeZone` on the same reply), not the tablet's zone.
// The slots are `gpsSampleSlotMinutes` below — for 05:30–07:00 every 5:
// 05:25 (the lead reading), 05:30, 05:35 … 07:00, the END INCLUSIVE. ABSENT
// keys mean "no opinion, keep what you hold" (the sync rule — see
// packages/api/src/services/field.syncOpinions.ts); both come from the
// company setting `faceRecognition.gpsSampling` (Settings → Attendance →
// "GPS readings on tablets").
//
// ── What the device sends ────────────────────────────────────────────────────
//
//   POST /api/field/gps-samples
//   Authorization: the field device token, exactly as on /field/events.
//   Body: FieldGpsSamplesRequest — { samples: FieldGpsSample[] }, 1..500.
//
//   takenAt   ISO-8601 WITH an offset or `Z` — the device clock when the
//             reading STARTED, the same clock a punch's `ts` is. A reading
//             more than 5 minutes ahead of the server (the punch path's
//             FIELD_TS_MAX_FUTURE_MS) or older than the 60-day retention is
//             refused — counted in `rejected`. A punch that far ahead is
//             re-stamped at sync time; a reading cannot be, because the slot
//             it belongs to IS its time.
//   ok        true = a position came back. `false` = no fix: say why in
//             `reason` (NO_FIX_TIMEOUT, PERMISSION, LOCATION_OFF, …:
//             upper-case letters, digits and `_`, at most 40).
//   lat/lng   required when ok, both or neither otherwise.
//   accuracyM the fix's reported horizontal accuracy, metres.
//   provider  'gps' | 'network' | 'fused' | 'passive' | 'other'.
//   timeToFixMs  from request to fix (or to giving up).
//   batteryPct / charging / screenOn  the device's state at the reading.
//
//   IDEMPOTENT by (device, takenAt): resend a batch until it is answered; a
//   reading the server already holds is counted in `duplicate`, never stored
//   twice. Each sample is validated ON ITS OWN — one malformed reading is
//   counted in `rejected` and the rest are stored — so a 200 always means
//   "forget this batch": nothing in it will ever be accepted on a retry.
//   A 413/422 on the ENVELOPE (not an array, over 500) is a client bug.
//
//   Response 200: FieldGpsSamplesResponse — { received, stored, duplicate, rejected }.
//   An older server answers 404: keep the readings (capped) and retry later.
//
// No person can be expressed in this shape — a device's position and state,
// never a worker, a code or a face.
// ============================================================

export const GPS_SAMPLE_PROVIDERS = ['gps', 'network', 'fused', 'passive', 'other'] as const;
export type GpsSampleProvider = (typeof GPS_SAMPLE_PROVIDERS)[number];

/** Most readings one upload may carry. A full day of two windows is ~70. */
export const GPS_SAMPLES_MAX_BATCH = 500;

/** A reading is a SUCCESS when it came back with accuracy at or under this. */
export const GPS_SAMPLE_GOOD_ACCURACY_M = 25;

/** What a reason may spell: a token, so it cannot carry a name. */
export const GPS_SAMPLE_REASON_RE = /^[A-Z0-9_]{1,40}$/;

export const FieldGpsSampleSchema = z
  .object({
    takenAt: z.string().datetime({ offset: true }),
    ok: z.boolean(),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    accuracyM: z.number().min(0).max(100_000).optional(),
    provider: z.enum(GPS_SAMPLE_PROVIDERS).optional(),
    timeToFixMs: z.number().int().min(0).max(3_600_000).optional(),
    reason: z.string().regex(GPS_SAMPLE_REASON_RE).optional(),
    batteryPct: z.number().int().min(0).max(100).optional(),
    charging: z.boolean().optional(),
    screenOn: z.boolean().optional(),
  })
  .strict()
  .refine((s) => (s.lat === undefined) === (s.lng === undefined), {
    message: 'lat and lng come together',
    path: ['lng'],
  })
  .refine((s) => !s.ok || (s.lat !== undefined && s.lng !== undefined), {
    message: 'A reading that is ok carries lat and lng',
    path: ['lat'],
  });
export type FieldGpsSample = z.infer<typeof FieldGpsSampleSchema>;

/** The body, strictly — for the device to check itself before sending. */
export const FieldGpsSamplesRequestSchema = z.object({
  samples: z.array(FieldGpsSampleSchema).min(1).max(GPS_SAMPLES_MAX_BATCH),
});
export type FieldGpsSamplesRequest = z.infer<typeof FieldGpsSamplesRequestSchema>;

/** What the SERVER validates whole: the envelope. Each sample is then parsed
 *  with FieldGpsSampleSchema on its own, so one bad reading cannot poison the batch. */
export const FieldGpsSamplesEnvelopeSchema = z.object({
  samples: z.array(z.unknown()).min(1).max(GPS_SAMPLES_MAX_BATCH),
});

export interface FieldGpsSamplesResponse {
  /** Readings in the body. */
  received: number;
  /** Newly stored. */
  stored: number;
  /** Already held for this device at this `takenAt`. */
  duplicate: number;
  /** Malformed, or a clock the server will not store. Never accepted on a retry. */
  rejected: number;
}

/** One window on the device wire. */
export interface GpsSampleWindow {
  start: string;
  end: string;
}

/** The two keys every /field/sync page carries (absent = keep what you hold). */
export interface FieldGpsSamplingSync {
  gpsSampleWindows?: GpsSampleWindow[];
  gpsSampleEveryMin?: number;
}

/** "HH:MM" → minutes after midnight. */
export function hhmmMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Minutes after midnight → "HH:MM". */
export function minutesHhmm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Every slot of one window, as minutes after company midnight: the lead
 * reading `everyMin` before `start`, then `start`, `start + everyMin`, … up to
 * and INCLUDING `end` when the step lands on it. The device and the console
 * read the same list, so "expected" means the same thing at both ends.
 */
export function gpsSampleSlotMinutes(window: GpsSampleWindow, everyMin: number): number[] {
  const start = hhmmMinutes(window.start);
  const end = hhmmMinutes(window.end);
  const slots = [start - everyMin];
  for (let m = start; m <= end; m += everyMin) slots.push(m);
  return slots;
}

// ── The console's read: GET /api/field-gps/devices?range=24h (ADMIN) ────────
// The SAME period as the Performance section it is drawn inside — one control
// on the device screen, so one range schema (fieldPerf.schema.ts).

export const FieldGpsHealthQuerySchema = z.object({
  range: FieldPerfRangeSchema.default('24h'),
});

/** A slot with no successful reading. `reason` null = no reading arrived at all. */
export interface FieldGpsMiss {
  /** Company calendar day, YYYY-MM-DD. */
  day: string;
  /** Company wall-clock slot, HH:MM. */
  slot: string;
  reason: string | null;
}

export interface FieldGpsWindowFigures {
  /** Slots due in the range (rolling, its slots cut in company time), from
   *  the tablet's first reading on. */
  expected: number;
  /** Readings that arrived for a slot of this window. */
  taken: number;
  /** Of those, ok with accuracy ≤ GPS_SAMPLE_GOOD_ACCURACY_M. */
  succeeded: number;
  /** succeeded / taken, 0..1; null when nothing was taken. */
  successRate: number | null;
  medianFixMs: number | null;
  medianAccuracyM: number | null;
  /** Of the successful readings judged against the site fence, the share
   *  outside it, 0..1; null when the site has no fence or nothing was judged. */
  outsideFence: number | null;
  /** The newest first, capped; `missesTotal` is the uncapped count. */
  misses: FieldGpsMiss[];
  missesTotal: number;
}

export interface FieldGpsHealthRow {
  deviceId: string;
  name: string;
  worksiteName: string | null;
  /** The newest reading ever received; null = this tablet has sent none. */
  lastSampleAt: string | null;
  morning: FieldGpsWindowFigures;
  evening: FieldGpsWindowFigures;
}

export interface FieldGpsHealthResponse {
  range: FieldPerfRange;
  /** The windows the figures were cut by — the current setting. */
  windows: { morning: GpsSampleWindow; evening: GpsSampleWindow };
  everyMin: number;
  generatedAt: string;
  /** Worst first; tablets that never sent a reading last. */
  rows: FieldGpsHealthRow[];
}
